import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthState, LoginCredentials, LoginResponse } from '../types/auth.types';
import { authService } from '../services/auth.service';

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function storedSession(): LoginResponse | null {
  try {
    const value = localStorage.getItem('auth_session');
    return value ? JSON.parse(value) as LoginResponse : null;
  } catch {
    return null;
  }
}

function persistSession(session: LoginResponse): void {
  localStorage.setItem('auth_token', session.token);
  localStorage.setItem('auth_session', JSON.stringify(session));
}

function removeHandoffFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('handoff');
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialSession = storedSession();
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('auth_token'),
    accessToken: initialSession?.accessToken ?? null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Adopt a legacy-admin session first; otherwise validate the locally stored session.
  useEffect(() => {
    const checkAuth = async () => {
      const handoff = new URLSearchParams(window.location.search).get('handoff');
      if (handoff) {
        removeHandoffFromUrl();
        try {
          const session = await authService.exchangeHandoff(handoff);
          persistSession(session);
          setState({
            user: session.user,
            token: session.token,
            accessToken: session.accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        } catch {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_session');
          setState({
            user: null,
            token: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }
      }

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const user = await authService.getCurrentUser();
        setState({
          user,
          token,
          accessToken: storedSession()?.accessToken ?? null,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_session');
        setState({
          user: null,
          token: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuth();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const session = await authService.login(credentials);
      const { user, token, accessToken } = session;
      persistSession(session);
      setState({
        user,
        token,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_session');
    setState({
      user: null,
      token: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
    }),
    [state, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
